/**
 * Página de error de pago
 * 
 * Esta página se muestra cuando un pago con Redsys falla o es cancelado.
 * Muestra información del error y opciones para reintentar o continuar navegando.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import paymentService from '../services/paymentService';
import { toast } from 'react-toastify';
import '../styles/payment-result.css';

const PaymentError = () => {
  const { transactionId } = useParams();
  const _navigate = useNavigate();
  const _user = useAuth();

  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const loadTransactionData = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        if (!transactionId) {
          throw new Error('ID de transacción no proporcionado');
        }

        // Obtener detalles de la transacción
        const response = await paymentService.getTransactionById(transactionId);
        
        if (!response.success) {
          throw new Error(response.message || 'Error al cargar la transacción');
        }

        setTransaction(response.data);

        // Mostrar mensaje de error
        toast.error('El pago no pudo ser completado');

      } catch (error) {
        console.error('Error al cargar datos de la transacción:', error);
        setLoadError(error.message || 'Error al cargar la información del pago');
      } finally {
        setLoading(false);
      }
    };

    loadTransactionData();
  }, [transactionId]);

  /**
   * Formatear precio
   */
  const formatPrice = (price, currency = 'EUR') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  /**
   * Formatear fecha
   */
  const formatDate = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Obtener URL de redirección según el tipo de transacción
   */
  const getRetryUrl = () => {
    if (!transaction) return '/';

    switch (transaction.type) {
      case 'wallet_deposit':
        return '/wallet';
      case 'product_purchase':
        return transaction.relatedTo?.id ? `/products/${transaction.relatedTo.id}/checkout` : '/products';
      case 'match_payment':
        return transaction.relatedTo?.id ? `/match/${transaction.relatedTo.id}/checkout` : '/matches';
      default:
        return '/';
    }
  };

  /**
   * Obtener mensaje de error personalizado
   */
  const getErrorMessage = () => {
    if (!transaction) return 'El pago no pudo ser completado';

    // Si hay un mensaje específico del gateway
    if (transaction.gateway?.responseMessage) {
      return transaction.gateway.responseMessage;
    }

    // Si hay una razón de fallo
    if (transaction.failureReason) {
      return transaction.failureReason;
    }

    // Mensaje genérico según el tipo
    switch (transaction.type) {
      case 'wallet_deposit':
        return 'No se pudieron añadir fondos a tu monedero';
      case 'product_purchase':
        return 'No se pudo completar la compra del producto';
      case 'match_payment':
        return 'No se pudo completar la inscripción al partido';
      default:
        return 'El pago no pudo ser completado';
    }
  };

  /**
   * Obtener sugerencias según el tipo de error
   */
  const getErrorSuggestions = () => {
    const suggestions = [
      'Verifica que los datos de tu tarjeta sean correctos',
      'Asegúrate de tener saldo suficiente en tu cuenta',
      'Contacta con tu banco si el problema persiste',
      'Intenta usar otro método de pago'
    ];

    if (transaction?.gateway?.responseCode) {
      // Sugerencias específicas según el código de error de Redsys
      switch (transaction.gateway.responseCode) {
        case '0101':
          return ['Tu tarjeta ha caducado. Por favor, usa una tarjeta válida.'];
        case '0129':
          return ['El código de seguridad (CVV) es incorrecto. Verifica los datos de tu tarjeta.'];
        case '0190':
          return ['Tu banco ha denegado la transacción. Contacta con tu entidad bancaria.'];
        case '0191':
          return ['La fecha de caducidad es incorrecta. Verifica los datos de tu tarjeta.'];
        default:
          return suggestions;
      }
    }

    return suggestions;
  };

  if (loading) {
    return (
      <div className="payment-result-page">
        <div className="container">
          <div className="payment-result-card">
            <div className="loading">
              <div className="spinner"></div>
              <h2>Verificando pago...</h2>
              <p>Por favor, espera mientras verificamos tu transacción.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="payment-result-page">
        <div className="container">
          <div className="payment-result-card error">
            <div className="result-icon">
              <span className="material-icons">error</span>
            </div>
            <h2>Error al verificar el pago</h2>
            <p>{loadError}</p>
            <div className="result-actions">
              <Link to="/wallet" className="btn btn-primary">
                Ir al Monedero
              </Link>
              <Link to="/" className="btn btn-secondary">
                Ir al Inicio
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-result-page">
      <div className="container">
        <div className="payment-result-card error">
          <div className="result-icon">
            <span className="material-icons">error</span>
          </div>
          
          <h2>Pago No Completado</h2>
          <p className="error-message">{getErrorMessage()}</p>

          {transaction && (
            <div className="transaction-details">
              <h3>Detalles de la Transacción</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">ID de Transacción:</span>
                  <span className="value">{transaction.transactionId}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Monto:</span>
                  <span className="value">{formatPrice(transaction.amount, transaction.currency)}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Fecha:</span>
                  <span className="value">{formatDate(transaction.createdAt)}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Descripción:</span>
                  <span className="value">{transaction.description}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Estado:</span>
                  <span className="value status-failed">
                    {transaction.status === 'cancelled' ? 'Cancelado' : 'Fallido'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="error-suggestions">
            <h3>¿Qué puedes hacer?</h3>
            <ul>
              {getErrorSuggestions().map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))}
            </ul>
          </div>

          <div className="result-actions">
            <Link to={getRetryUrl()} className="btn btn-primary">
              <span className="material-icons">refresh</span>
              Intentar de Nuevo
            </Link>
            <Link to="/wallet" className="btn btn-secondary">
              Ir al Monedero
            </Link>
            <Link to="/" className="btn btn-secondary">
              Ir al Inicio
            </Link>
          </div>

          <div className="support-notice">
            <p>
              Si necesitas ayuda, puedes{' '}
              <Link to="/contact" className="support-link">
                contactar con nuestro soporte
              </Link>
              {' '}proporcionando el ID de transacción.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentError;
